import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/useAuth';
import HelpdeskWidget from './components/HelpdeskWidget';
import { lazyWithReload } from './utils/lazyWithReload';
import { isPlatformHost } from './utils/platformHost';

const Layout = lazyWithReload(() => import('./components/Layout'));
const AdminLayout = lazyWithReload(() => import('./components/admin/AdminLayout'));
const Home = lazyWithReload(() => import('./pages/Home'));
const Login = lazyWithReload(() => import('./pages/Login'));
const Register = lazyWithReload(() => import('./pages/Register'));
const SetPassword = lazyWithReload(() => import('./pages/SetPassword'));
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('./pages/ResetPassword'));
const Terms = lazyWithReload(() => import('./pages/Terms'));
const Privacy = lazyWithReload(() => import('./pages/Privacy'));
const Units = lazyWithReload(() => import('./pages/Units'));
const Bookings = lazyWithReload(() => import('./pages/Bookings'));
const BookingDetails = lazyWithReload(() => import('./pages/BookingDetails'));
const Profile = lazyWithReload(() => import('./pages/Profile'));
const Payments = lazyWithReload(() => import('./pages/Payments'));
const Billing = lazyWithReload(() => import('./pages/Billing'));
const Statements = lazyWithReload(() => import('./pages/Statements'));
const Support = lazyWithReload(() => import('./pages/Support'));
const Documents = lazyWithReload(() => import('./pages/Documents'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const AdminDashboard = lazyWithReload(() => import('./pages/admin/Dashboard'));
const AdminUnits = lazyWithReload(() => import('./pages/admin/Units'));
const AdminBookings = lazyWithReload(() => import('./pages/admin/Bookings'));
const AdminCustomers = lazyWithReload(() => import('./pages/admin/Customers'));
const AdminAccounting = lazyWithReload(() => import('./pages/admin/Accounting'));
const AdminBilling = lazyWithReload(() => import('./pages/admin/billing/Billing'));
const AdminBillingStatementDetails = lazyWithReload(() => import('./pages/admin/billing/BillingStatementDetails'));
const AdminBillingReminders = lazyWithReload(() => import('./pages/admin/billing/BillingReminders'));
const AdminBillingScheduling = lazyWithReload(() => import('./pages/admin/billing/BillingScheduling'));
const AdminSettings = lazyWithReload(() => import('./pages/admin/Settings'));
const AdminTenants = lazyWithReload(() => import('./pages/admin/Tenants'));
const AdminUsers = lazyWithReload(() => import('./pages/admin/AdminUsers'));
const AdminAccounts = lazyWithReload(() => import('./pages/admin/Accounts'));
const AdminNotificationLogs = lazyWithReload(() => import('./pages/admin/NotificationLogs'));
const AdminOnlineUsers = lazyWithReload(() => import('./pages/admin/OnlineUsers'));
const AdminLoginActivity = lazyWithReload(() => import('./pages/admin/LoginActivity'));
const AdminAuditLog = lazyWithReload(() => import('./pages/admin/AuditLog'));
const AdminSystemStatus = lazyWithReload(() => import('./pages/admin/SystemStatus'));
const AdminAppManagement = lazyWithReload(() => import('./pages/admin/AppManagement'));
const AdminHelp = lazyWithReload(() => import('./pages/admin/Help'));
const AdminSupportTickets = lazyWithReload(() => import('./pages/admin/SupportTickets'));
const AdminDocuments = lazyWithReload(() => import('./pages/admin/Documents'));
const AdminLeasing = lazyWithReload(() => import('./pages/admin/Leasing'));
const AdminLeasingDetails = lazyWithReload(() => import('./pages/admin/LeasingDetails'));
const Apply = lazyWithReload(() => import('./pages/Apply'));

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
}

function CustomerRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user && ['TENANT_ADMIN', 'SUPERADMIN'].includes(user.role)) {
    return <Navigate to="/admin" />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (!['TENANT_ADMIN', 'SUPERADMIN'].includes(user.role)) return <Navigate to="/" />;

  return <>{children}</>;
}

export default function App() {
  const routeFallback = (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <Suspense fallback={routeFallback}>
      <HelpdeskWidget />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        {/* Customers sign up with their own company; the product's site has nobody to sign them up with. */}
        <Route path="/register" element={isPlatformHost() ? <Navigate to="/" replace /> : <Register />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* Lease application / e-sign via private link (no login) */}
        <Route path="/apply/:token" element={<Apply />} />

      {/* Protected routes */}
        <Route
          path="/"
          element={
            <CustomerRoute>
              <Layout />
            </CustomerRoute>
          }
        >
          <Route index element={<Home />} />
          <Route
            path="dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route path="units" element={<Units />} />
          <Route
            path="bookings"
            element={
              <PrivateRoute>
                <Bookings />
              </PrivateRoute>
            }
          />
          <Route
            path="bookings/:id"
            element={
              <PrivateRoute>
                <BookingDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="payments"
            element={
              <PrivateRoute>
                <Payments />
              </PrivateRoute>
            }
          />
          <Route
            path="billing"
            element={
              <PrivateRoute>
                <Billing />
              </PrivateRoute>
            }
          />
          <Route
            path="statements"
            element={
              <PrivateRoute>
                <Statements />
              </PrivateRoute>
            }
          />
          <Route
            path="support"
            element={
              <PrivateRoute>
                <Support />
              </PrivateRoute>
            }
          />
          <Route
            path="documents"
            element={
              <PrivateRoute>
                <Documents />
              </PrivateRoute>
            }
          />

        </Route>

        {/* Admin routes with AdminLayout */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="accounting" element={<AdminAccounting />} />
          <Route path="billing" element={<AdminBilling />} />
          <Route path="billing/statements/:id" element={<AdminBillingStatementDetails />} />
          <Route path="billing/reminders" element={<AdminBillingReminders />} />
          <Route path="billing/scheduling" element={<AdminBillingScheduling />} />
          <Route path="invoicing/*" element={<Navigate to="/admin/billing" replace />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="units" element={<AdminUnits />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="support" element={<AdminSupportTickets />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="help" element={<AdminHelp />} />
          <Route path="notifications" element={<AdminNotificationLogs />} />
          <Route path="online-users" element={<AdminOnlineUsers />} />
          <Route path="login-activity" element={<AdminLoginActivity />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="system-status" element={<AdminSystemStatus />} />
          <Route path="app-management" element={<AdminAppManagement />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="documents" element={<AdminDocuments />} />
          <Route path="leasing" element={<AdminLeasing />} />
          <Route path="leasing/:id" element={<AdminLeasingDetails />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
